<script setup lang="ts">
    import remend from 'remend'
    import { marked, type Token } from 'marked'
    import {
        computed,
        nextTick,
        onBeforeUnmount,
        onMounted,
        useTemplateRef,
        watch,
    } from 'vue'
    import { useI18n } from 'vue-i18n'
    import {
        loadCdnCss,
        getShadowRoot,
        isSameSite,
        stripProviderCitationMarkers,
    } from 'utils'
    import { useShortUrlResolver } from 'composables'

    export type StreamingAnimation =
        'fadeIn' | 'blurIn' | 'dropIn' | 'slideUp' | 'fadeAndScale' | null

    const props = withDefaults(
        defineProps<{
            markdown?: string
            loading?: boolean
            animation?: StreamingAnimation
            animationDuration?: string
            isDark?: boolean
        }>(),
        {
            markdown: '',
            loading: false,
            animation: 'fadeIn',
            animationDuration: '0.3s',
            isDark: false,
        },
    )

    const { t: $t } = useI18n({ useScope: 'global' })

    // --- Lazy-loaded heavy dependencies ---

    let beautifulMermaidPromise: Promise<
        typeof import('beautiful-mermaid')
    > | null = null
    function loadBeautifulMermaid() {
        if (!beautifulMermaidPromise) {
            beautifulMermaidPromise = import('beautiful-mermaid')
        }
        return beautifulMermaidPromise
    }

    let katexPromise: Promise<(typeof import('katex'))['default']> | null = null
    function loadKatex() {
        if (!katexPromise) {
            katexPromise = import('katex').then((mod) => mod.default)
        }
        return katexPromise
    }

    // --- Marked extensions ---

    const inlineKatexRule =
        /^(\${1,2})(?!\$)((?:\\.|[^\\\n])*?(?:\\.|[^\\\n$]))\1(?=[\s?!.,:\uFF1F\uFF01\u3002\uFF0C\uFF1A]|$)/
    const blockKatexRule = /^(\${1,2})\n((?:\\[^]|[^\\])+?)\n\1(?:\n|$)/

    function encodeKatexHtml(text: string): string {
        return text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
    }

    function renderKatexPlaceholder(
        text: string,
        displayMode: boolean,
    ): string {
        const encoded = encodeKatexHtml(text)
        return `<span class="katex-pending" data-katex="${encoded}" data-display="${displayMode}"></span>`
    }

    marked.use({
        extensions: [
            {
                name: 'inlineKatex',
                level: 'inline',
                start: (src) => {
                    const idx = src.indexOf('$')
                    if (idx === -1) return undefined
                    if (idx === 0 || src.charAt(idx - 1) === ' ') return idx
                    return undefined
                },
                tokenizer(src) {
                    const match = src.match(inlineKatexRule)
                    if (!match) return
                    return {
                        type: 'inlineKatex',
                        raw: match[0],
                        text: match[2].trim(),
                        displayMode: match[1].length === 2,
                    }
                },
                renderer(token) {
                    return renderKatexPlaceholder(token.text, token.displayMode)
                },
            },
            {
                name: 'blockKatex',
                level: 'block',
                tokenizer(src) {
                    const match = src.match(blockKatexRule)
                    if (!match) return
                    return {
                        type: 'blockKatex',
                        raw: match[0],
                        text: match[2].trim(),
                        displayMode: match[1].length === 2,
                    }
                },
                renderer(token) {
                    return (
                        renderKatexPlaceholder(token.text, token.displayMode) +
                        '\n'
                    )
                },
            },
        ],
    })

    marked.use({
        extensions: [
            {
                name: 'thinking',
                level: 'block',
                start: (src) => src.indexOf('<thinking>'),
                tokenizer(src) {
                    const match = /^<thinking>([\s\S]*?)<\/thinking>/.exec(src)
                    if (!match) return
                    const token = {
                        type: 'thinking',
                        raw: match[0],
                        text: match[1].trim(),
                        tokens: [] as Token[],
                    }
                    this.lexer.blockTokens(token.text, token.tokens)
                    return token
                },
                renderer(token) {
                    return `<div class="thinking-block">${this.parser.parse(token.tokens ?? [])}</div>`
                },
            },
        ],
    })

    // --- Custom renderer ---

    const renderer = new marked.Renderer()

    // marked escapes href and title inside its own link renderer. This
    // override replaces that renderer, so until now both went into `v-html`
    // verbatim: a quote in the URL closed the attribute and everything after
    // it became markup, in a document written by a model and, through the
    // knowledge base, by whoever fed it.
    const escapeAttribute = (value: string) =>
        value
            .replace(/&/g, '&amp;')
            .replace(/"/g, '&quot;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
    // Schemes that execute rather than navigate. The link becomes plain text,
    // so the reader still sees what it said.
    const UNSAFE_LINK_SCHEME = /^(?:javascript|data|vbscript|file):/i

    renderer.link = function ({ href, title, tokens }) {
        const text = this.parser.parseInline(tokens)
        // Control characters go first: the browser strips them before reading
        // the scheme, so they are how `java\tscript:` gets past a check.
        // eslint-disable-next-line no-control-regex -- they are the point here
        const target = (href ?? '').trim().replace(/[\x00-\x1f]/g, '')
        if (!target || UNSAFE_LINK_SCHEME.test(target)) {
            return text
        }
        const titleAttr = title ? ` title="${escapeAttribute(title)}"` : ''
        const targetAttrs = isSameSite(target)
            ? ''
            : ' target="_blank" rel="noopener noreferrer"'
        return `<a href="${escapeAttribute(target)}"${titleAttr}${targetAttrs}>${text}</a>`
    }

    renderer.table = function (token) {
        return `<div class="table-wrapper">${marked.Renderer.prototype.table.call(this, token)}</div>`
    }

    const MERMAID_PREFIXES =
        /^(sequenceDiagram|graph|classDiagram|stateDiagram|erDiagram|flowchart|xychart-beta)/

    renderer.code = ({ text }) => {
        if (MERMAID_PREFIXES.test(text)) {
            return `<pre class="mermaid">${text}</pre>`
        }
        return `<pre><code>${text}</code></pre>`
    }

    // --- Preprocessing pipeline ---

    function normalizeLatexDelimiters(md: string): string {
        return md
            .replace(/\\\[([\s\S]*?)\\\]/g, (_, content) => `$$${content}$$`)
            .replace(/\\\(([\s\S]*?)\\\)/g, (_, content) => `$${content}$`)
            .replace(/^(\d+)\.(?!\s)/gm, '$1\\.')
    }

    /**
     * Strips JSON objects/arrays that appear outside fenced code blocks.
     * Some models dump tool-call JSON directly into the text response.
     */
    function stripLeakedJson(md: string): string {
        const fenced = new Set<number>()
        const fenceRe = /^(`{3,})[^\n]*\n([\s\S]*?)\n\1/gm
        let fenceMatch: RegExpExecArray | null
        while ((fenceMatch = fenceRe.exec(md)) !== null) {
            for (
                let i = fenceMatch.index;
                i < fenceMatch.index + fenceMatch[0].length;
                i++
            ) {
                fenced.add(i)
            }
        }

        return md
            .replace(
                /[{[]([\s\S]*?)[}\]]/g,
                (match, _inner, offset: number) => {
                    if (fenced.has(offset)) return match
                    try {
                        JSON.parse(match)
                        return ''
                    } catch {
                        return match
                    }
                },
            )
            .replace(/\n{3,}/g, '\n\n')
    }

    // Runs on the accumulated text, not on deltas, so a marker split across two
    // stream chunks still gets caught once both have landed.
    const preprocessors: Array<(md: string) => string> = [
        stripProviderCitationMarkers,
        stripLeakedJson,
        remend,
        normalizeLatexDelimiters,
    ]

    // --- Computed HTML ---

    const containerRef = useTemplateRef<HTMLDivElement>('container')

    // Expand backend short urls (`/api/r/<hash>`) back to their real target so
    // prose (e.g. a bulleted list of links) shows/opens real urls, not the
    // token-cheap short ones the model sees. No-op when no resolver is provided.
    const { resolveText } = useShortUrlResolver()
    const expandedMarkdown = resolveText(() => props.markdown)

    const html = computed(() => {
        const md = preprocessors.reduce(
            (text, fn) => fn(text),
            expandedMarkdown.value,
        )
        return marked.parse(md, {
            async: false,
            breaks: true,
            renderer,
        }) as string
    })

    // --- Post-render processors ---

    async function renderMermaid() {
        if (props.loading) return

        await nextTick()
        const nodes = containerRef.value?.querySelectorAll<HTMLElement>(
            'pre.mermaid:not([data-rendered])',
        )
        if (!nodes?.length) return

        const { renderMermaidSVG, THEMES } = await loadBeautifulMermaid()
        const theme = props.isDark
            ? THEMES['github-dark']
            : THEMES['github-light']

        for (const node of Array.from(nodes)) {
            if (!node.isConnected) continue
            const code = node.textContent?.trim()
            if (!code) continue

            try {
                const svg = renderMermaidSVG(code, theme)
                if (node.isConnected) {
                    node.dataset.code = code
                    node.innerHTML = svg
                    node.setAttribute('data-rendered', 'true')
                }
            } catch {
                // Invalid syntax — node left untouched
            }
        }
    }

    async function highlightCode() {
        await nextTick()
        const blocks = containerRef.value?.querySelectorAll<HTMLElement>(
            'pre:not(.mermaid) > code:not([data-highlighted])',
        )
        if (!blocks?.length) return

        const hljs = (await import('highlight.js/lib/common')).default
        for (const block of blocks) {
            hljs.highlightElement(block)
            block.setAttribute('data-highlighted', 'true')
        }
    }

    async function renderKatex() {
        await nextTick()
        const nodes =
            containerRef.value?.querySelectorAll<HTMLElement>('.katex-pending')
        if (!nodes?.length) return

        const katex = await loadKatex()
        // Keep this major in step with the installed katex: 0.18 prefixed the
        // internal class names (.sizing became .katex-sizing), so a stylesheet
        // from another major styles nothing. The widget pins the same major
        // again in its vite config, where katex is aliased to the CDN.
        loadCdnCss(
            'https://cdn.jsdelivr.net/npm/katex@0.18/dist/katex.min.css',
            getShadowRoot(containerRef.value),
        )

        for (const node of Array.from(nodes)) {
            if (!node.isConnected) continue
            const tex = node.dataset.katex
            if (!tex) continue
            const displayMode = node.dataset.display === 'true'
            try {
                node.innerHTML = katex.renderToString(tex, {
                    throwOnError: false,
                    displayMode,
                })
                node.classList.remove('katex-pending')
            } catch {
                // Invalid LaTeX — leave placeholder
            }
        }
    }

    async function rerenderMermaidOnThemeChange() {
        await nextTick()
        const nodes = containerRef.value?.querySelectorAll<HTMLElement>(
            'pre.mermaid[data-rendered]',
        )
        if (!nodes?.length) return

        const { renderMermaidSVG, THEMES } = await loadBeautifulMermaid()
        const theme = props.isDark
            ? THEMES['github-dark']
            : THEMES['github-light']

        for (const node of Array.from(nodes)) {
            if (!node.isConnected) continue
            const code = node.dataset.code
            if (!code) continue
            try {
                node.innerHTML = renderMermaidSVG(code, theme)
            } catch {
                // Invalid syntax — leave as-is
            }
        }
    }

    /**
     * Markdown images come from model output (or from a scraped page), so their
     * urls die often. The markup is injected with `v-html`, so there is no place
     * to bind `@error`: `error` doesn't bubble but it does propagate in the
     * capture phase, so one container listener covers every image, including the
     * ones added by later stream chunks.
     */
    function handleImageError(event: Event) {
        const img = event.target
        if (!(img instanceof HTMLImageElement) || img.dataset.broken) {
            return
        }
        img.dataset.broken = 'true'
        const placeholder = document.createElement('span')
        placeholder.className = 'markdown-broken-image'
        placeholder.textContent = img.alt || $t('label.imageLoadError')
        placeholder.title = img.src
        img.replaceWith(placeholder)
    }

    onMounted(() => {
        containerRef.value?.addEventListener('error', handleImageError, true)
    })
    onBeforeUnmount(() => {
        containerRef.value?.removeEventListener('error', handleImageError, true)
    })

    watch([html, () => props.loading], renderMermaid, { immediate: true })
    watch(() => props.isDark, rerenderMermaidOnThemeChange)
    watch(html, highlightCode, { immediate: true })
    watch(html, renderKatex, { immediate: true })

    // --- Streaming animation (FlowToken-style) ---

    let prevCharCount = 0

    function createAnimatedSpan(text: string, delay: number): HTMLSpanElement {
        const span = document.createElement('span')
        span.style.cssText = `animation:ft-${props.animation} ${props.animationDuration} ease-out ${delay}ms both;display:inline-block`
        span.textContent = text
        span.addEventListener(
            'animationend',
            () => {
                const parent = span.parentNode
                if (!parent) return
                parent.replaceChild(document.createTextNode(text), span)
                parent.normalize()
            },
            { once: true },
        )
        return span
    }

    function insertAnimatedWords(
        text: string,
        parent: Node,
        refNode: Node | null,
        startIndex: number,
    ): number {
        let idx = startIndex
        const parts = text.split(/(\s+)/)
        for (const part of parts) {
            if (!part) continue
            if (/^\s+$/.test(part)) {
                parent.insertBefore(document.createTextNode(part), refNode)
                continue
            }
            parent.insertBefore(createAnimatedSpan(part, idx * 30), refNode)
            idx++
        }
        return idx
    }

    function collectTextNodes(root: HTMLElement): Text[] {
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
            acceptNode(node) {
                if (
                    node.parentElement?.closest(
                        'pre, code, .katex-pending, .markdown-broken-image',
                    )
                ) {
                    return NodeFilter.FILTER_REJECT
                }
                return NodeFilter.FILTER_ACCEPT
            },
        })
        const nodes: Text[] = []
        while (walker.nextNode()) {
            nodes.push(walker.currentNode as Text)
        }
        return nodes
    }

    function annotateNewContent() {
        if (!containerRef.value || !props.loading || !props.animation) return

        const textNodes = collectTextNodes(containerRef.value)
        let charsSeen = 0
        let wordIdx = 0

        for (const textNode of textNodes) {
            const text = textNode.textContent || ''
            const nodeStart = charsSeen
            const nodeEnd = charsSeen + text.length
            charsSeen = nodeEnd

            if (nodeEnd <= prevCharCount) continue

            const parent = textNode.parentNode
            if (!parent) continue

            // Entirely new node — animate all words
            if (nodeStart >= prevCharCount) {
                const ref = textNode.nextSibling
                parent.removeChild(textNode)
                wordIdx = insertAnimatedWords(text, parent, ref, wordIdx)
                continue
            }

            // Partially new — split and animate only the new portion
            const splitAt = prevCharCount - nodeStart
            const newTextNode = textNode.splitText(splitAt)
            const newText = newTextNode.textContent || ''
            const ref = newTextNode.nextSibling
            parent.removeChild(newTextNode)
            wordIdx = insertAnimatedWords(newText, parent, ref, wordIdx)
        }

        prevCharCount = charsSeen
    }

    watch(
        html,
        async () => {
            await nextTick()
            annotateNewContent()
        },
        { immediate: true },
    )

    watch(
        () => props.markdown,
        (newVal, oldVal) => {
            if (!newVal || (oldVal && newVal.length < oldVal.length)) {
                prevCharCount = 0
            }
        },
    )
</script>

<template>
    <div ref="container" v-html="html"></div>
</template>

<style>
    .markdown-broken-image {
        display: inline-flex;
        align-items: center;
        gap: var(--spacing-4);
        max-width: 100%;
        padding: var(--spacing-4) var(--spacing-8);
        border: var(--border) dashed var(--color-surface-4);
        border-radius: var(--rounded-md);
        background-color: var(--color-surface-1);
        color: var(--color-word-4);
        font-size: var(--text-12);
        vertical-align: middle;
    }

    @keyframes ft-fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    @keyframes ft-blurIn {
        from {
            opacity: 0;
            filter: blur(4px);
        }
        to {
            opacity: 1;
            filter: blur(0px);
        }
    }

    @keyframes ft-dropIn {
        from {
            transform: translateY(-8px);
            opacity: 0;
        }
        to {
            transform: translateY(0);
            opacity: 1;
        }
    }

    @keyframes ft-slideUp {
        from {
            transform: translateY(8px);
            opacity: 0;
        }
        to {
            transform: translateY(0);
            opacity: 1;
        }
    }

    @keyframes ft-fadeAndScale {
        from {
            transform: scale(0.85);
            opacity: 0;
        }
        to {
            transform: scale(1);
            opacity: 1;
        }
    }
</style>
